<template>
	<v-dialog v-model="dialog" max-width="1200" persistent scrollable>
		<v-card class="elevation-12 rounded-xl">
			<v-toolbar color="primary" class="darken-1" flat dense border-bottom>
				<v-icon left color="white">mdi-file-import</v-icon>
				<span class="subtitle-1 font-weight-bold white--text text--darken-3">Actualización Masiva de Lotes</span>
				<v-spacer></v-spacer>
				<v-btn icon small @click="cancelImportLogs" :disabled="isImporting">
					<v-icon color="white">mdi-close</v-icon>
				</v-btn>
			</v-toolbar>

			<v-card-text class="pa-6">
				<v-container fluid class="pa-0">
					<v-row justify="center" align="center">
						<v-col cols="12" sm="10" md="8">
							<v-hover v-slot="{ hover }">
								<div :class="[
									'upload-area',
									{ 'hovering': hover },
									{ 'file-ready': importFile }
								]" @click="$refs.fileInput.click()">
									
									<input type="file" ref="fileInput" style="display: none"
										accept=".xlsx,.xls" @change="onFileChange">

									<div class="upload-content text-center py-8">
										<v-icon size="64"
											:color="importFile ? 'success' : (hover ? 'primary' : 'grey')"
											class="mb-4">
											{{ importFile ? 'mdi-file-check' : 'mdi-cloud-upload' }}
										</v-icon>

										<div v-if="!importFile">
											<div class="text-h6 font-weight-bold grey--text text--darken-2">
												Seleccionar archivo Excel
											</div>
											<div class="text-subtitle-2 grey--text mt-1">
												Haz clic para buscar el archivo .xlsx
											</div>
											<div class="caption grey--text mt-2">
												Cabeceras requeridas: id, hectareas, hectareas_totales, plantas, caudal, cilindros
											</div>
										</div>

										<div v-else>
											<div class="text-h6 font-weight-bold success--text">
												{{ importFile.name }}
											</div>
											<div class="text-subtitle-2 success--text mt-1">
												Archivo cargado correctamente
											</div>
										</div>

										<v-progress-linear v-if="isLoadingFile" indeterminate absolute bottom
											color="primary"></v-progress-linear>
									</div>
								</div>
							</v-hover>
						</v-col>
					</v-row>

					<v-col cols="12">
						<v-row class="mt-0" justify="center" align="center">
							<v-btn v-if="processedLogs.length > 0" color="error" depressed outlined
								@click="resetImport" :disabled="isImporting" class="mr-4">
								<v-icon left>mdi-refresh</v-icon> Limpiar Importación
							</v-btn>
						</v-row>
					</v-col>

					<v-divider class="my-2"></v-divider>

					<v-row v-if="isLoadingFile">
						<v-col cols="12" class="text-center py-8">
							<v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
							<div class="mt-4 grey--text text--darken-1 font-weight-medium text-subtitle-1">
								Procesando archivo...
							</div>
						</v-col>
					</v-row>

					<div v-else>
						<div v-if="processedLogs.length > 0">
							<div class="d-flex align-center justify-space-between my-2">
								<h4 class="subtitle-1 font-weight-medium">
									<v-icon color="success" class="mr-1">mdi-check-circle-outline</v-icon>
									Lotes a actualizar: <span class="primary--text">
										{{ processedLogs.length }}</span>
								</h4>
								<v-btn small outlined color="info" @click="downloadTemplate" :disabled="isImporting">
									<v-icon left small>mdi-download</v-icon>
									Descargar Plantilla
								</v-btn>
							</div>

							<v-data-table :headers="importHeaders" :items="processedLogs" :items-per-page="25"
								dense class="elevation-1 rounded-lg border-table" mobile-breakpoint="0">
								<template v-slot:[`item.FltHectares`]="{ item }">
									{{ item.FltHectares }}
								</template>
								<template v-slot:[`item.FltHectaresFull`]="{ item }">
									{{ item.FltHectaresFull }}
								</template>
								<template v-slot:[`item.FltPlants`]="{ item }">
									{{ item.FltPlants }}
								</template>
								<template v-slot:[`item.FltFlow`]="{ item }">
									{{ item.FltFlow }}
								</template>
								<template v-slot:[`item.FltCylinder`]="{ item }">
									{{ item.FltCylinder }}
								</template>
								<template v-slot:[`item.FltSowingYear`]="{ item }">
									{{ item.FltSowingYear }}
								</template>
							</v-data-table>
						</div>

						<v-row v-else-if="importFile" class="text-center py-12">
							<v-col cols="12">
								<v-icon size="80" color="grey lighten-2">mdi-file-search-outline</v-icon>
								<h3 class="grey--text text--darken-1 mt-4">Sin resultados</h3>
								<p class="grey--text">No se encontraron líneas que coincidan con el formato esperado.</p>
							</v-col>
						</v-row>

						<v-row v-else class="text-center py-12">
							<v-col cols="12">
								<v-icon size="80" color="grey lighten-3">mdi-cloud-upload-outline</v-icon>
								<h3 class="grey--text lighten-2 mt-4">Sube un archivo para empezar</h3>
								<p class="grey--text lighten-2">Analizaremos el contenido del excel para la actualización masiva de los campos del Lote.</p>
								<v-btn text small color="primary" @click="downloadTemplate" class="mt-2">
									<v-icon left small>mdi-file-excel-outline</v-icon> Descargar Plantilla Modelo
								</v-btn>
							</v-col>
						</v-row>
					</div>
				</v-container>
			</v-card-text>

			<v-card-actions class="pa-4 grey lighten-5 border-top">
				<v-spacer></v-spacer>
				<v-btn text color="grey darken-2" @click="cancelImportLogs" :disabled="isImporting">
					Cancelar
				</v-btn>
				<v-btn color="info" class="elevation-2 font-weight-medium px-6" @click="importLogs"
					:disabled="processedLogs.length === 0 || isLoadingFile" :loading="isImporting">
					<v-icon left>mdi-cloud-upload</v-icon>
					Actualizar Lotes
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import _sFarmLot from '@/services/Technicalassistance/FarmLotService.js';
import ExcelJS from "exceljs";

export default {
    name: 'FarmLotImportModal',
    props: {
        value: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            importFile: null,
            processedLogs: [],
            isImporting: false,
            isLoadingFile: false,
            importHeaders: [
                { text: 'ID Lote', value: 'FltID', align: 'center' },
                { text: 'Hectáreas', value: 'FltHectares', align: 'right' },
                { text: 'Hectáreas Totales', value: 'FltHectaresFull', align: 'right' },
                { text: 'Plantas', value: 'FltPlants', align: 'right' },
                { text: 'Año Siembra', value: 'FltSowingYear', align: 'center' },
                { text: 'Caudal', value: 'FltFlow', align: 'right' },
                { text: 'Cilindros', value: 'FltCylinder', align: 'right' },
            ],
        };
    },
    computed: {
        dialog: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val);
            }
        }
    },
    watch: {
        dialog(val) {
            if (val) {
                this.resetImport();
            }
        }
    },
    methods: {
        cancelImportLogs() {
            this.dialog = false;
            this.resetImport();
        },
        resetImport() {
            this.importFile = null;
            this.processedLogs = [];
            this.isLoadingFile = false;
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }
        },
        onFileChange(e) {
            const files = e.target.files;
            if (files.length > 0) {
                const file = files[0];
                this.importFile = file;
                const extension = file.name.split('.').pop().toLowerCase();
                if (extension === 'xlsx' || extension === 'xls') {
                    this.handleExcelUpload();
                } else {
                    this.$fun.alert("Solo se admiten documentos Excel (.xlsx, .xls)", "warning");
                    this.resetImport();
                }
            }
        },
        async handleExcelUpload() {
            if (!this.importFile) {
                this.processedLogs = [];
                return;
            }
            this.isLoadingFile = true;
            try {
                const workbook = new ExcelJS.Workbook();
                const reader = new FileReader();
                const buffer = await new Promise((resolve, reject) => {
                    reader.onload = (e) => resolve(e.target.result);
                    reader.onerror = (e) => reject(e);
                    reader.readAsArrayBuffer(this.importFile);
                });
                await workbook.xlsx.load(buffer);
                const worksheet = workbook.getWorksheet(1);
                const results = [];
                
                const expectedHeaders = ['id', 'total plantas', 'hectareas certificadas', 'hectareas totales (m)', 'caudal', 'cilindros', 'año de siembra'];
                let headerRowIndex = -1;
                let colIndices = { fltID: -1, plants: -1, hectares: -1, hectaresFull: -1, flow: -1, cylinder: -1, sowingYear: -1 };
                
                worksheet.eachRow((row, rowNumber) => {
                    if (headerRowIndex !== -1) return;
                    const rowValues = row.values;
                    if (Array.isArray(rowValues)) {
                        const foundHeaders = rowValues.map(v => (v ? v.toString().toLowerCase().trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "") : ''));
                        const matches = expectedHeaders.filter(h => foundHeaders.includes(h));
                        
                        // Si encuentra mas de 2 cabeceras validas o es la primera fila, lo aceptamos
                        if (matches.length >= 2 || rowNumber === 1 || rowNumber === 2) {
                            headerRowIndex = rowNumber;
                            colIndices.fltID = foundHeaders.indexOf('id');
                            colIndices.plants = foundHeaders.indexOf('total plantas');
                            colIndices.hectares = foundHeaders.indexOf('hectareas certificadas');
                            colIndices.hectaresFull = foundHeaders.indexOf('hectareas totales (m)');
                            colIndices.flow = foundHeaders.indexOf('caudal');
                            colIndices.cylinder = foundHeaders.indexOf('cilindros');
                            colIndices.sowingYear = foundHeaders.indexOf('ano de siembra'); // Note: normalized without tilde
                        }
                    }
                });
                
                if (headerRowIndex === -1 || colIndices.fltID === -1) {
                    this.$fun.alert("No se encontraron los encabezados mínimos requeridos ('ID', 'Total Plantas... etc')", "warning");
                    this.resetImport();
                    return;
                }
                
                worksheet.eachRow((row, rowNumber) => {
                    if (rowNumber <= headerRowIndex) return;
                    const rowValues = row.values;
                    
                    const idVal = rowValues[colIndices.fltID];
                    if (!idVal) return;
                    
                    const fId = parseInt(idVal.toString());
                    if (isNaN(fId) || fId <= 0) return;

                    const parseFloatSafe = (val) => {
                         if (!val && val !== 0) return 0;
                         const parsed = parseFloat(val.toString().replace(',','.'));
                         return isNaN(parsed) ? 0 : parsed;
                    };
                    
                    const parseIntSafe = (val) => {
                         if (!val && val !== 0) return 0;
                         const parsed = parseInt(val.toString());
                         return isNaN(parsed) ? 0 : parsed;
                    };

                    results.push({
                        FltID: fId,
                        FltHectares: colIndices.hectares !== -1 ? parseFloatSafe(rowValues[colIndices.hectares]) : 0,
                        FltHectaresFull: colIndices.hectaresFull !== -1 ? parseFloatSafe(rowValues[colIndices.hectaresFull]) : 0,
                        FltPlants: colIndices.plants !== -1 ? parseIntSafe(rowValues[colIndices.plants]) : 0,
                        FltSowingYear: colIndices.sowingYear !== -1 ? parseIntSafe(rowValues[colIndices.sowingYear]) : 0,
                        FltFlow: colIndices.flow !== -1 ? parseFloatSafe(rowValues[colIndices.flow]) : 0,
                        FltCylinder: colIndices.cylinder !== -1 ? parseFloatSafe(rowValues[colIndices.cylinder]) : 0,
                    });
                });
                
                this.processedLogs = results;
                if (results.length === 0) {
                    this.$fun.alert("No se encontraron registros de Lotes válidos", "info");
                }
            } catch (error) {
                console.error("Error processing Excel:", error);
                this.$fun.alert("Error al procesar el archivo Excel", "error");
            } finally {
                this.isLoadingFile = false;
            }
        },
        importLogs() {
            if (this.processedLogs.length === 0) {
                this.$fun.alert("No hay registros para importar", "warning");
                return;
            }
            
            this.$fun.alert("¿Está seguro de realizar la actualización masiva de estos Lotes?", "question")
                .then(r => {
                    if (r) {
                        this.isImporting = true;
                        try {
                            _sFarmLot.updateMassive(this.processedLogs, this.$fun.getUserID()).then(response => {
                                if (response && response.status === 200) {
                                    this.$fun.alert(`Actualización masiva de Lotes realizada correctamente`, 'success');
                                    this.$emit('imported');
                                    this.dialog = false;
                                    this.resetImport();
                                }
                            }).catch(err => {
                                 this.$fun.alert("Ocurrió un error general durante la actualización " + (err.response?.data?.Message || err.message), "error");
                            }).finally(()=> {
                                 this.isImporting = false;
                            });
                        } catch (globalErr) {
                            this.$fun.alert("Ocurrió un error general durante la petición", "error");
                            this.isImporting = false;
                        }
                    }
                });
        },
        downloadTemplate() {
            const workbook = new ExcelJS.Workbook();
            const sheet = workbook.addWorksheet('Plantilla Lotes', {
                views: [{ state: 'frozen', xSplit: 0, ySplit: 1 }]
            });
            
            sheet.columns = [
                { header: 'ID', key: 'FltID', width: 10 },
                { header: 'Total Plantas', key: 'FltPlants', width: 15 },
                { header: 'Hectareas Certificadas', key: 'FltHectares', width: 20 },
                { header: 'Hectareas Totales (m)', key: 'FltHectaresFull', width: 22 },
                { header: 'Año de Siembra', key: 'FltSowingYear', width: 15 },
                { header: 'Caudal', key: 'FltFlow', width: 15 },
                { header: 'Cilindros', key: 'FltCylinder', width: 15 }
            ];
            
            const headerRow = sheet.getRow(1);
            headerRow.font = { bold: true };
            headerRow.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor:{ argb:'FFD3D3D3' }
            };
            
            sheet.addRow({ FltID: 1234, FltPlants: 300, FltHectares: 10.5, FltHectaresFull: 15.0, FltSowingYear: 2021, FltFlow: 2.5, FltCylinder: 5.0 });
            sheet.addRow({ FltID: 1235, FltPlants: 150, FltHectares: 8.2, FltHectaresFull: 10.0, FltSowingYear: 2019, FltFlow: 1.2, FltCylinder: 3.0 });
            
            workbook.xlsx.writeBuffer().then((data) => {
                const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const url = window.URL.createObjectURL(blob);
                const anchor = document.createElement('a');
                anchor.href = url;
                anchor.download = 'Plantilla_Actualizacion_Lotes.xlsx';
                anchor.click();
                window.URL.revokeObjectURL(url);
            });
        }
    }
};
</script>
<style scoped>
.rounded-xl {
	border-radius: 16px !important;
}
.upload-area {
	border: 2px dashed #E0E0E0;
	border-radius: 16px;
	cursor: pointer;
	transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
	background-color: #fafafa;
	position: relative;
	overflow: hidden;
}
.upload-area.hovering {
	border-color: #3f51b5;
	background-color: #f0f2ff;
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}
.upload-area.file-ready {
	border-style: solid;
	border-color: #4CAF50;
	background-color: #f1f8e9;
}
</style>
